import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { User as UserIcon, Save, Search, X, Check } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import ProfilePhotoWidget from "@/components/ProfilePhotoWidget";
import ProfileAddress from "@/components/ProfileAddress";
import NotificationBell from "@/components/NotificationBell";
import { BBC_LOGO_URL } from "@/lib/brand";

const KIND_LABEL = {
  spouse: "Spouse", father: "Father", mother: "Mother",
  son: "Son", daughter: "Daughter", brother: "Brother", sister: "Sister",
  other_family: "Other family",
};
const FAMILY_KINDS = ["father","mother","son","daughter","brother","sister","other_family"];

export default function MyProfile() {
  const { user: authUser, loading: authLoading, checkAuth: refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [ministries, setMinistries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [rels, setRels] = useState([]);
  const [dir, setDir] = useState([]);
  const [showFinder, setShowFinder] = useState(null); // {kind}
  const [q, setQ] = useState("");

  const load = async () => {
    const [{ data: mine }, { data: opts }] = await Promise.all([api.get("/auth/me"), api.get("/ministries")]);
    setMe(mine); setMinistries(opts.options || []);
    if (mine.role === "visitor") { setRels([]); setDir([]); return; }
    try {
      const [{ data: myRels }, { data: directory }] = await Promise.all([api.get("/relationships/mine"), api.get("/directory")]);
      setRels(myRels); setDir(directory || []);
    } catch { setRels([]); setDir([]); }
  };

  useEffect(() => { load(); }, []);
  if (authLoading) return <div className="min-h-screen flex items-center justify-center">Loading profile…</div>;
  if (!authUser) return <Navigate to="/admin/login" replace />;
  if (!me) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] text-[#5C6661]">Loading…</div>;

  const toggleMinistry = (m) => setMe({ ...me, ministries: me.ministries?.includes(m) ? me.ministries.filter(x=>x!==m) : [...(me.ministries||[]), m] });
  const setPhone = (i, v) => { const p = [...(me.phones||[])]; p[i] = v; setMe({ ...me, phones: p }); };
  const addPhone = () => setMe({ ...me, phones: [...(me.phones||[]), ""] });
  const removePhone = (i) => setMe({ ...me, phones: (me.phones||[]).filter((_,x)=>x!==i) });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/users/me", {
        name: me.name, address: me.address, phones: (me.phones||[]).filter(Boolean),
        birthday: me.birthday || null, sex: me.sex || null, marital_status: me.marital_status || null, ministries: me.ministries || [],
      });
      setMe(data); refresh(); toast.success("Profile saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const request = async (other_user_id, kind) => {
    try { await api.post("/relationships", { other_user_id, kind }); toast.success("Request sent"); setShowFinder(null); setQ(""); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const decide = async (id, action) => {
    try {
      if (action === "remove") await api.delete(`/relationships/${id}`);
      else await api.put(`/relationships/${id}/${action}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const pending_in = rels.filter(r => r.status === "pending" && r.my_role === "target");
  const pending_out = rels.filter(r => r.status === "pending" && r.my_role === "requester");
  const accepted = rels.filter(r => r.status === "accepted");
  const spouse = accepted.find(r => r.my_kind === "spouse");
  const profileComplete = Boolean(me.birthday && me.sex && me.marital_status && me.phones?.some(p => p?.trim()) && (typeof me.address === "string" ? me.address.trim() : me.address?.line1));
  const filtered = dir.filter(u => !q || u.name?.toLowerCase().includes(q.toLowerCase()) || u.email?.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="my-profile">
      <header className="bg-white border-b border-[#E5E0D8]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5"><img src={BBC_LOGO_URL} alt="" className="w-8 h-8" /><span className="font-serif-display text-xl text-[#2C4C3B]">My Profile</span></div>
          <div className="flex gap-4 text-sm items-center">
            <Link to="/" className="text-[#5C6661] hover:text-[#2C4C3B] font-medium">Home</Link>
            {me.role !== "visitor" && <Link to="/messages" className="text-[#5C6661] hover:text-[#2C4C3B] font-medium">Messages</Link>}
            {(me.role === "super_admin" || me.role === "admin" || me.role === "staff") && <Link to="/admin" className="text-[#5C6661] hover:text-[#2C4C3B] font-medium">CMS</Link>}
            <NotificationBell />
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <div className="p-6 bg-white border border-[#E5E0D8] rounded-2xl">
          <div className="flex items-center gap-5">
            <ProfilePhotoWidget user={me} onChange={(u) => { setMe({ ...me, picture: u.picture }); refresh(); }} editable />
            <div>
              <h1 className="font-serif-display text-3xl text-[#1C201E]">{me.name}</h1>
              <div className="text-sm text-[#5C6661]">{me.email} · {me.role === "bible_school_student" ? "Bible School Student" : me.role === "visitor" ? "Visitor" : me.role} {me.role !== "visitor" && <>· <span className="uppercase text-xs tracking-widest">{me.member_status || "active"}</span></>}</div>
            </div>
          </div>
        </div>

        {me.role === "visitor" && <div className="p-4 bg-white border border-[#E5E0D8] rounded-xl text-sm text-[#5C6661]">Visitor accounts have access to the Bible, Daily Verse, and Daily Devotion.</div>}
        {me.role === "member" && me.verification_status === "pending" && <div className="p-5 bg-amber-50 border border-amber-200 rounded-xl"><h2 className="font-serif-display text-xl">{profileComplete ? "Verification Pending" : "Are you a member of BBC Barani?"}</h2><p className="mt-2 text-sm text-[#5C6661]">{profileComplete ? "Your profile information has been submitted and is awaiting review." : "Complete your profile details so your membership can be reviewed and verified."}</p></div>}

        {me.role !== "visitor" && <div className="p-6 bg-white border border-[#E5E0D8] rounded-2xl">
          <h2 className="font-serif-display text-2xl text-[#1C201E] mb-4">Personal details</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Name (required)"><input data-testid="p-name" value={me.name||""} onChange={e=>setMe({...me,name:e.target.value})} className="input" /></Field>
            <Field label="Sex"><select data-testid="p-sex" value={me.sex||""} onChange={e=>setMe({...me,sex:e.target.value})} className="input"><option value="">—</option><option value="M">Male</option><option value="F">Female</option></select></Field>
            <Field label="Birthday"><input data-testid="p-birthday" type="date" value={me.birthday||""} onChange={e=>setMe({...me,birthday:e.target.value})} className="input" /></Field>
            <Field label="Marital status"><select data-testid="p-marital-status" value={me.marital_status||""} onChange={e=>setMe({...me,marital_status:e.target.value||null})} className="input"><option value="">—</option><option>Single</option><option>Married</option><option>Widow</option><option>Separated</option></select></Field>
            <Field label={`Age (auto): ${me.age ?? "—"}`}><input disabled value={me.age ?? ""} className="input bg-[#F3EFEA] text-[#5C6661]" /></Field>
            <div className="sm:col-span-2"><p className="mb-2 text-sm">Address</p><ProfileAddress value={me.address} onChange={address => setMe(current => ({ ...current, address }))}/></div>
          </div>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-widest text-[#5C6661]">Phone numbers</label>
            <div className="mt-2 space-y-2">
              {(me.phones||[]).map((ph, i) => (
                <div key={i} className="flex gap-2">
                  <input data-testid={`p-phone-${i}`} value={ph} onChange={e=>setPhone(i,e.target.value)} className="input flex-1" placeholder="+63 917 ..." />
                  <button onClick={()=>removePhone(i)} className="px-3 rounded-full border border-red-200 text-red-600 text-xs">Remove</button>
                </div>
              ))}
              <button data-testid="p-add-phone" onClick={addPhone} className="px-3 py-1.5 rounded-full border border-[#E5E0D8] text-sm text-[#1C201E]">+ Add phone</button>
            </div>
          </div>
        </div>}

        {me.role !== "visitor" && <div className="p-6 bg-white border border-[#E5E0D8] rounded-2xl">
          <h2 className="font-serif-display text-2xl text-[#1C201E] mb-2">Ministry involvement</h2>
          <p className="text-xs text-[#5C6661] mb-3">Select all that apply. This helps the leadership plan events and rosters.</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {ministries.map(m => (
              <label key={m} data-testid={`p-ministry-${m}`} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E5E0D8] hover:bg-[#F3EFEA] cursor-pointer">
                <input type="checkbox" checked={me.ministries?.includes(m)||false} onChange={()=>toggleMinistry(m)} />
                <span className="text-sm text-[#1C201E]">{m}</span>
              </label>
            ))}
          </div>
        </div>}

        {me.role !== "visitor" && <div className="p-6 bg-white border border-[#E5E0D8] rounded-2xl">
          <div className="flex items-center justify-between mb-3"><h2 className="font-serif-display text-2xl text-[#1C201E]">Spouse</h2>{!spouse && me.role !== "pending" && <button data-testid="p-add-spouse" onClick={()=>setShowFinder({kind:"spouse"})} className="px-4 py-1.5 rounded-full bg-[#2C4C3B] text-white text-xs">+ Link spouse</button>}</div>
          {spouse ? <RelRow rel={spouse} onDecide={decide} /> : <div className="text-sm text-[#5C6661]">No spouse linked.</div>}
        </div>}

        {me.role !== "visitor" && <div className="p-6 bg-white border border-[#E5E0D8] rounded-2xl">
          <div className="flex items-center justify-between mb-3"><h2 className="font-serif-display text-2xl text-[#1C201E]">Family</h2>{me.role !== "pending" && <button data-testid="p-add-family" onClick={()=>setShowFinder({kind:"father"})} className="px-4 py-1.5 rounded-full bg-[#2C4C3B] text-white text-xs">+ Link family</button>}</div>
          <div className="space-y-2">
            {accepted.filter(r=>r.my_kind!=="spouse").map(r => <RelRow key={r.id} rel={r} onDecide={decide} />)}
            {accepted.filter(r=>r.my_kind!=="spouse").length === 0 && <div className="text-sm text-[#5C6661]">No family links yet.</div>}
          </div>
        </div>}

        {me.role !== "visitor" && (pending_in.length + pending_out.length) > 0 && (
          <div className="p-6 bg-white border border-[#E5E0D8] rounded-2xl">
            <h2 className="font-serif-display text-2xl text-[#1C201E] mb-3">Pending requests</h2>
            {pending_in.map(r=><RelRow key={r.id} rel={r} onDecide={decide} incoming />)}
            {pending_out.map(r=><RelRow key={r.id} rel={r} onDecide={decide} outgoing />)}
          </div>
        )}

        <button data-testid="p-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] disabled:opacity-60"><Save className="w-4 h-4" /> {saving?"Saving…":"Save profile"}</button>

        {showFinder && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={()=>setShowFinder(null)}>
            <div onClick={e=>e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-lg w-full">
              <h3 className="font-serif-display text-2xl text-[#1C201E] mb-1">Link {showFinder.kind === "spouse" ? "spouse" : "family member"}</h3>
              {showFinder.kind !== "spouse" && (
                <select data-testid="finder-kind" value={showFinder.kind} onChange={e=>setShowFinder({kind:e.target.value})} className="input mt-3 mb-2">
                  {FAMILY_KINDS.map(k => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                </select>
              )}
              <div className="relative"><Search className="w-4 h-4 absolute left-3 top-3 text-[#5C6661]" /><input data-testid="finder-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search church directory…" className="input pl-9" /></div>
              <div className="mt-3 max-h-72 overflow-y-auto divide-y divide-[#E5E0D8] border border-[#E5E0D8] rounded-lg">
                {filtered.map(u => (
                  <button key={u.user_id} data-testid={`finder-pick-${u.user_id}`} onClick={()=>request(u.user_id, showFinder.kind)} className="w-full text-left px-3 py-2 hover:bg-[#F3EFEA] text-sm">
                    <div className="font-medium text-[#1C201E]">{u.name}</div><div className="text-xs text-[#5C6661]">{u.email}</div>
                  </button>
                ))}
                {filtered.length === 0 && <div className="p-4 text-sm text-[#5C6661]">No matches.</div>}
              </div>
              <div className="mt-3 text-right"><button onClick={()=>setShowFinder(null)} className="text-sm text-[#5C6661]">Cancel</button></div>
            </div>
          </div>
        )}
      </div>
      <style>{`.input{width:100%;padding:.5rem .75rem;border:1px solid #E5E0D8;border-radius:.75rem;background:white}.input:focus{outline:2px solid #D4A373}`}</style>
    </div>
  );
}

function Field({ label, children, full }) {
  return <label className={`flex flex-col gap-1 ${full?"md:col-span-2":""}`}><span className="text-xs uppercase tracking-widest text-[#5C6661]">{label}</span>{children}</label>;
}

function RelRow({ rel, onDecide, incoming, outgoing }) {
  return (
    <div data-testid={`rel-${rel.id}`} className="flex items-center justify-between p-3 rounded-xl border border-[#E5E0D8]">
      <div className="flex items-center gap-3">
        {rel.other.picture ? <img src={rel.other.picture.startsWith('/api/')?process.env.REACT_APP_BACKEND_URL+rel.other.picture:rel.other.picture} className="w-9 h-9 rounded-full object-cover" alt="" /> : <div className="w-9 h-9 rounded-full bg-[#F3EFEA] flex items-center justify-center text-[#5C6661]">{(rel.other.name||"?").slice(0,1)}</div>}
        <div>
          <div className="text-sm font-medium text-[#1C201E]">{rel.other.name || rel.other.email}</div>
          <div className="text-xs text-[#5C6661]">{KIND_LABEL[rel.my_kind]||rel.my_kind}{incoming?" · wants to link":""}{outgoing?" · awaiting reply":""}</div>
        </div>
      </div>
      <div className="flex gap-2">
        {incoming && (<><button data-testid={`rel-accept-${rel.id}`} onClick={()=>onDecide(rel.id, "accept")} className="px-3 py-1.5 rounded-full bg-[#2C4C3B] text-white text-xs inline-flex items-center gap-1"><Check className="w-3 h-3" /> Accept</button><button data-testid={`rel-decline-${rel.id}`} onClick={()=>onDecide(rel.id, "decline")} className="px-3 py-1.5 rounded-full border border-[#E5E0D8] text-[#1C201E] text-xs">Decline</button></>)}
        {(rel.status === "accepted" || outgoing) && <button data-testid={`rel-remove-${rel.id}`} onClick={()=>onDecide(rel.id, "remove")} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Remove"><X className="w-4 h-4" /></button>}
      </div>
    </div>
  );
}
